import { apiRequest } from './api';

/**
 * Bildirimler — CLAUDE.md §7.8.
 *
 * İki ayrı yetenek var, karıştırılmamalı:
 * - **Yerel bildirim:** uygulama kapalıyken bile cihazın kendi
 *   gösterdiği bildirim. Yalnızca izin ister, sunucu gerekmez.
 * - **Push bildirimi:** sunucunun (Cloudflare Worker) uzaktan
 *   gönderdiği bildirim; günlük hatırlatma bunu kullanır. VAPID
 *   anahtarı ve yayınlanmış bir backend gerektirir.
 *
 * İzin bir kez alınır, ikisi de aynı izni kullanır.
 */

/**
 * İskeletle gelen yer tutucu. Gerçek anahtar girilene kadar push
 * kurulamaz.
 *
 * `: string` açıklamasının sebebi için bkz. `api.ts` — TypeScript'in
 * karşılaştırmayı derleme anında çözüp hata saymasını (TS2367) engeller.
 */
const VAPID_PLACEHOLDER: string = 'REPLACE_WITH_YOUR_VAPID_PUBLIC_KEY';

/** Yayınlanmış Worker'ın VAPID açık anahtarı — wrangler.toml ile aynı olmalı. */
const VAPID_PUBLIC_KEY = 'BEQOIHgUutSYPuDNTX-HABYOMuxz_2H0W-1qfuFrmsU44B6hq8cmRqJ08x_HdIYs0KIM3abqi2KVNgEbPgTXbXE';

function urlBase64ToUint8Array(base64String: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = atob(base64);

  const output = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i += 1) {
    output[i] = rawData.charCodeAt(i);
  }
  return output;
}

/** Bildirim kurulumunun sonucu — ekranın ne yazacağını belirler. */
export type NotificationSetupResult =
  | {
      ok: true;
      /** Uzaktan push da kuruldu mu? (VAPID/backend hazır değilse false) */
      pushRegistered: boolean;
    }
  | { ok: false; reason: 'unsupported' | 'denied' | 'pushFailed' };

/** Bildirim izni bu cihazda verilmiş mi? */
export function hasNotificationPermission(): boolean {
  return 'Notification' in window && Notification.permission === 'granted';
}

/** Kullanıcı izni kalıcı olarak reddetmiş mi? (Ayarlar'dan açması gerekir) */
export function isNotificationBlocked(): boolean {
  return 'Notification' in window && Notification.permission === 'denied';
}

/**
 * Bildirim iznini ister ve mümkünse cihazı push'a abone eder.
 *
 * iOS'ta bu ancak uygulama ANA EKRANA EKLENDİYSE çalışır; Safari
 * sekmesinde `Notification` nesnesi bulunmaz ve `unsupported` döner.
 *
 * İzin alındıysa `ok: true` döner — VAPID anahtarı henüz girilmemiş
 * olsa bile. Sebep: izin tek başına yerel bildirimler (streak
 * kutlaması) için yeterli, push ayrı bir aşama.
 */
export async function enableNotifications(): Promise<NotificationSetupResult> {
  if (
    !('serviceWorker' in navigator) ||
    !('PushManager' in window) ||
    !('Notification' in window)
  ) {
    return { ok: false, reason: 'unsupported' };
  }

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    return { ok: false, reason: 'denied' };
  }

  // VAPID anahtarı hâlâ yer tutucuysa abone olmayı DENEMİYORUZ:
  // `atob` geçersiz metinde hata fırlatır ve kullanıcı, aslında
  // başarıyla verdiği izni başarısız sanırdı.
  if (VAPID_PUBLIC_KEY === VAPID_PLACEHOLDER) {
    return { ok: true, pushRegistered: false };
  }

  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
    });

    await apiRequest('/subscribe', {
      method: 'POST',
      body: { subscription },
    });

    return { ok: true, pushRegistered: true };
  } catch (error) {
    console.error('Push aboneliği kurulamadı:', error);
    return { ok: false, reason: 'pushFailed' };
  }
}

/**
 * Seri arttığında bildirim gösterir.
 *
 * YALNIZCA uygulama arka plandayken gösterilir. Uygulama önplandaysa
 * kullanıcı zaten tam ekran kutlamayı görüyor; üstüne bir de bildirim
 * bandı düşürmek gereksiz tekrar olurdu.
 *
 * Bu bir PUSH değil, YEREL bildirimdir — ve öyle olmak zorunda:
 * serinin arttığı, gün kapanışı sırasında cihazın kendisinde
 * hesaplanıyor (bkz. `dailyRollover.ts`). Sunucunun haberi yok, o
 * yüzden tetikleyecek bir sunucu olayı da yok.
 *
 * Ses verilemez (§7.8): iOS sistem varsayılan sesini çalar.
 */
export async function notifyStreakIncrease(streak: number): Promise<void> {
  if (!hasNotificationPermission()) return;
  if (document.visibilityState === 'visible') return;

  try {
    const registration = await navigator.serviceWorker.ready;
    await registration.showNotification(`${streak} günlük seri!`, {
      body: 'Seriyi sürdürdün. Bugün de devam et.',
      icon: '/icons/icon-192.png',
      // Aynı etiketle gelen yeni bildirim eskisinin yerini alır:
      // arka planda birkaç gün geçtiyse üst üste bildirim yığılmasın.
      tag: 'streak-increase',
    });
  } catch (error) {
    console.error('Streak bildirimi gösterilemedi:', error);
  }
}
